export const THEME_KEY = "lumika.theme";

export type Theme = "light" | "dark" | "system";

export function getStoredTheme(): Theme {
  if (typeof window === "undefined") return "system";
  try {
    const stored = window.localStorage.getItem(THEME_KEY);
    if (stored === "light" || stored === "dark" || stored === "system") {
      return stored;
    }
  } catch {
    // localStorage unavailable (e.g. Safari private mode) — fall back to system.
  }
  return "system";
}

export function resolveTheme(theme: Theme): "light" | "dark" {
  if (theme === "system") {
    return window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light";
  }
  return theme;
}

// Hex mirrors of --background in @lumika/ui styles.css (light 0 0% 98%,
// dark 0 0% 4%). Exported so the spec can assert parity with the static
// metas in index.html — same drift guard as THEME_KEY.
export const THEME_COLOR: Record<"light" | "dark", string> = {
  light: "#fafafa",
  dark: "#0a0a0a",
};

export function applyTheme(theme: Theme): void {
  const resolved = resolveTheme(theme);
  document.documentElement.classList.toggle("dark", resolved === "dark");
  // index.html ships media-query theme-color metas for first paint, but once
  // the user picks an explicit theme the app can disagree with the OS
  // preference — pin every meta to the resolved background so browser chrome
  // (iOS status bar, Android address bar) matches the app, not the OS.
  document
    .querySelectorAll<HTMLMetaElement>('meta[name="theme-color"]')
    .forEach((meta) => meta.setAttribute("content", THEME_COLOR[resolved]));
  try {
    window.localStorage.setItem(THEME_KEY, theme);
  } catch {
    // localStorage unavailable — the class is still applied for this session.
  }
}
